import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

import { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { FieldPath, useFormContext } from "react-hook-form";

import { dispatchSetEditorContents } from "src/base-components/EditorTable/utils";
import { DecisionTableOperators } from "src/clients/flow-api";
import { useOnClickOutside } from "src/hooks/usehooks-ts";
import { FloatingCodeInput } from "src/matrixTableNode/FloatingCodeInput";
import { MatrixTableNodeForm } from "src/matrixTableNode/MatrixTableNodeEditor";
import { useMatrixTableNavActions } from "src/matrixTableNode/stores";
import { SetValueFns } from "src/matrixTableNode/types";
import { isFloatingOperatorDropdown } from "src/matrixTableNode/utils";

type AttachProps = {
  event?: React.MouseEvent<HTMLDivElement>;
  ref: React.RefObject<HTMLElement | null>;
  mode: "effect" | "predicate";
  path: FieldPath<MatrixTableNodeForm>;
};

type ContextValue = {
  enabled: boolean;
  attach: (props: AttachProps) => void;
  detach: () => void;
};

const FloadingCodeInputContext = createContext<ContextValue | null>(null);

type Props = {
  children: React.ReactNode;
  enabled: boolean;
  tableRef: React.RefObject<HTMLDivElement | null>;
};

export type LastFieldRef = {
  path: FieldPath<MatrixTableNodeForm>;
  mode: "effect" | "predicate";
  operator: DecisionTableOperators;
  value: string;
};

export const FloatingCodeInputProvider = ({
  children,
  enabled,
  tableRef,
}: Props) => {
  const { reset } = useMatrixTableNavActions();
  const setValueRef = useRef<SetValueFns>(undefined);
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const lastFieldRef = useRef<LastFieldRef | null>(null);
  const { getValues, setValue } = useFormContext<MatrixTableNodeForm>();
  const [mode, setMode] = useState<"effect" | "predicate">("effect");
  const [trackingRef, setTrackingRef] =
    useState<React.RefObject<HTMLElement | null> | null>(null);

  const flushLastField = useCallback(() => {
    if (lastFieldRef.current) {
      const lastField = { ...lastFieldRef.current };
      if (lastField.mode === "predicate") {
        setValue(
          `${lastField.path}.value` as
            | "column_predicates.0.value"
            | "row_predicates.0.value",
          lastField.value,
        );

        setValue(
          `${lastField.path}.operator` as
            | "column_predicates.0.operator"
            | "row_predicates.0.operator",
          lastField.operator,
        );
      } else {
        setValue(lastField.path, lastField.value);
      }
    }
  }, [setValue]);

  const attach = useCallback(
    ({ mode, ref, path, event }: AttachProps) => {
      if (lastFieldRef.current?.path === path) {
        return;
      }

      // Flush the last field
      flushLastField();

      // Set the new field
      const formValue = getValues(path);
      const target = editorRef.current?.editor;
      let stringValue: string = "";

      if (
        mode === "predicate" &&
        typeof formValue === "object" &&
        "value" in formValue &&
        "operator" in formValue
      ) {
        setValueRef.current?.setValue(formValue.value);
        setValueRef.current?.setOperator(formValue.operator);

        stringValue = formValue.value;

        lastFieldRef.current = {
          path,
          mode,
          operator: formValue.operator,
          value: formValue.value,
        };
      }

      if (mode === "effect" && typeof formValue === "string") {
        setValueRef.current?.setValue(formValue);
        stringValue = formValue;
        lastFieldRef.current = {
          path,
          mode,
          operator: DecisionTableOperators.EQ,
          value: formValue,
        };
      }

      setMode(mode);
      setTrackingRef(ref);

      if (target) {
        let shouldFocus = true;
        if (event?.type === "click") {
          if (
            (event.target as unknown as HTMLElement)?.closest(
              ".dormant-operator-dropdown",
            )
          ) {
            const floatingDropdown = document.querySelector(
              "[data-loc='floating-operator-dropdown'] button",
            );
            if (floatingDropdown) {
              event.preventDefault();
              event.stopPropagation();

              // wait for floating input to be attached and then dispatch the event
              setTimeout(() => {
                floatingDropdown.dispatchEvent(
                  new Event(event.type, {
                    ...event,
                  }),
                );
              }, 1);

              shouldFocus = false;
            }
          } else {
            target.dispatchEvent(
              new Event(event.type, {
                ...event,
              }),
            );
          }
        }
        setTimeout(() => {
          if (editorRef.current) {
            dispatchSetEditorContents(
              editorRef.current,
              stringValue,
              shouldFocus,
            );
            editorRef.current.view?.requestMeasure();
          }
        }, 0);
      }
    },
    [getValues, flushLastField],
  );

  const detach = useCallback(
    (keepFocus?: boolean) => {
      flushLastField();
      setValueRef.current?.setValue("");
      setValueRef.current?.setOperator(DecisionTableOperators.EQ);
      setMode("predicate");
      setTrackingRef(null);
      lastFieldRef.current = null;
      reset("editingCell");

      if (editorRef.current?.view?.hasFocus) {
        editorRef.current?.view?.contentDOM.blur();
      }

      if (keepFocus) {
        tableRef.current?.focus();
      }
    },
    [flushLastField, reset, tableRef],
  );

  useOnClickOutside(tableRef, (e) => {
    if (!isFloatingOperatorDropdown(e)) {
      detach();
      reset();
    }
  });

  return (
    <FloadingCodeInputContext value={{ enabled, attach, detach }}>
      {enabled && (
        <FloatingCodeInput
          detach={detach}
          editorRef={editorRef}
          lastFieldRef={lastFieldRef}
          mode={mode}
          setValueRef={setValueRef}
          tableRef={tableRef}
          trackingRef={trackingRef}
        />
      )}
      {children}
    </FloadingCodeInputContext>
  );
};

export const useFloatingCodeInput = () => {
  const context = useContext(FloadingCodeInputContext);
  if (!context) {
    throw new Error(
      "useFloatingCodeInput must be used within a FloatingCodeInputProvider",
    );
  }
  return context;
};
